import store from '@state/store'
import { get } from 'lodash'
import { PermissionLevel } from '@/shared/constants/permissions/PermissionLevel'
import { PermissionScope } from '@/shared/constants/permissions/PermissionScope'
import { PermissionModifier } from '@/shared/constants/permissions/PermissionModifier'
import { PermissionRequirement } from '@/shared/constants/permissions/PermissionRequirement'
import { hasAccessToRoute } from '@/helpers/permissions-helpers.js'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'

export default {
  computed: {
    navItems() {
      return this.generateNavItems().filter((navItem) => {
        // Check feature flag if specified
        if (navItem.featureFlag) {
          const featureFlags = store.getters['features/featureFlags']
          if (!get(featureFlags, navItem.featureFlag, false)) return false
        }

        // Check required route perms if they exist. Display the nav item if perms are satisfied.
        if (Object.prototype.hasOwnProperty.call(navItem, 'permissions')) {
          return hasAccessToRoute(
            {
              meta: {
                permissions: navItem.permissions,
              },
            },
            store
          )
        }

        // If no permissions are set, display the nav item
        return true
      })
    },
    getActiveNavItem() {
      return this.navItems.find((navItem) => this.isActiveNavItem(navItem.to))
    },
  },
  methods: {
    generateNavItems() {
      return [
        {
          key: 'nav-home',
          title: this.$t('home.homePageTitle'),
          icon: 'mdi-home-outline',
          to: { name: routeDefinitions.home.name },
        },
        {
          key: 'nav-bookings',
          title: this.$t('bookingList.pageTitle'),
          icon: 'mdi-calendar-month',
          to: { name: routeDefinitions.bookingsList.name },
          featureFlag: 'selfService.enableBookingList',
          permissions: {
            requirement: PermissionRequirement.ONE,
            list: [
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.BOOKING,
                modifier: PermissionModifier.VIEW,
              },
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.PENDING_BOOKING,
                modifier: PermissionModifier.VIEW,
              },
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.PENDING_BOOKING,
                modifier: PermissionModifier.CREATE,
              },
            ],
          },
        },
        {
          key: 'nav-candidates',
          title: this.$t('candidates.pageTitle'),
          icon: 'mdi-account-group-outline',
          to: { name: routeDefinitions.candidates.name },
          permissions: {
            requirement: PermissionRequirement.ONE,
            list: [
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.BOOKING,
                modifier: PermissionModifier.VIEW,
              },
              {
                level: PermissionLevel.SELECTED_CLIENT_AND_BELOW,
                scope: PermissionScope.TIMESHEETS,
                modifier: PermissionModifier.VIEW,
              },
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.ACCOUNTS,
                modifier: PermissionModifier.VIEW,
              },
            ],
          },
        },
        {
          key: 'nav-pending-approvals',
          title: this.$t('booking.pendingApproval.shortTitle'),
          icon: 'mdi-calendar-check-outline',
          to: { name: routeDefinitions.bookingsPendingApproval.name },
          permissions: {
            requirement: PermissionRequirement.ONE,
            list: [
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.BOOKING,
                modifier: PermissionModifier.ALL,
              },
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.PENDING_BOOKING,
                modifier: PermissionModifier.VIEW,
              },
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.PENDING_BOOKING,
                modifier: PermissionModifier.CREATE,
              },
            ],
          },
        },
        {
          key: 'nav-timesheets',
          title: this.$t('timesheets.pageTitle'),
          icon: 'mdi-timer-outline',
          to: { name: routeDefinitions.timesheets.name },
          permissions: {
            requirement: PermissionRequirement.ALL,
            list: [
              {
                level: PermissionLevel.SELECTED_CLIENT_AND_BELOW,
                scope: PermissionScope.TIMESHEETS,
                modifier: PermissionModifier.VIEW,
              },
            ],
          },
        },
        {
          key: 'nav-finance',
          title: this.$t('finance.financePageTitle'),
          icon: 'mdi-currency-usd',
          to: { name: routeDefinitions.finance.name },
          permissions: {
            requirement: PermissionRequirement.ALL,
            list: [
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.ACCOUNTS,
                modifier: PermissionModifier.VIEW,
              },
            ],
          },
        },
      ]
    },

    isActiveNavItem(to) {
      return to.name === this.$route.name
    },
  },
}
